import { useMemo, useState } from 'react';
import { Avatar, Divider, Stack, Typography } from '@mui/material';

import AppSidebarItem, { AppSidebarItemWrapper } from '@common/ui/AppSidebarItem';
import Popover from '@common/ui/Popover';

import { getLogoutUrl } from '@common/libs/auth';
import { ThemeColorScheme, useThemeMode } from '@common/hooks/use-theme-mode.hook';

import UserThemeSwitcher from '../components/UserThemeSwitcher';
import UserSwitchTenant from '../components/UserSwitchTenant';
import { useFlagFixed } from '@common/utils/flags/use-flag-fixed';
import { Flags } from '@common/utils/flags/feature-flags';
import { useAponoAuth } from '@common/ui/Authenticated';
import routes from '@routes';

export default function User({ minimized }: { minimized?: boolean }) {
  const { user, tenants, switchTenant, showAdminPortal } = useAponoAuth();
  const [open, setOpen] = useState(false);
  const { prefersDarkMode, handleThemeModeSwitch } = useThemeMode();

  const { isEnabled: isRequireMfaEnabled } = useFlagFixed(Flags.REQUEST_MFA);
  const { isEnabled: isPersonalApiTokensEnabled } = useFlagFixed(Flags.CLIENT_PERSONAL_API_TOKENS);

  const handleOnTenantChange = (newTenantId: string) => {
    switchTenant({
      tenantId: newTenantId,
      callback: (_, err) => {
        if (err) throw new Error(`Unexpected error when switching tenants: ${err}`);
        window.location.reload();
      },
    });
  };

  const transformHeight = useMemo(() => {
    let baseHeight = 320;

    if (isRequireMfaEnabled) {
      baseHeight += 50;
    }

    if (isPersonalApiTokensEnabled) {
      baseHeight += 50;
    }

    return Math.abs(window.innerHeight - baseHeight);
  }, [isPersonalApiTokensEnabled, isRequireMfaEnabled]);

  return (
    <Popover
      isOpen={open}
      trigger={
        <AppSidebarItem
          icon={<Avatar alt={user.name} src={user.profilePictureUrl} sx={{ width: 24, height: 24 }} />}
          label={user.name}
          minimized={minimized}
          onClick={() => setOpen((prev) => !prev)}
          enableAnimation
        />
      }
      onClickOutside={() => setOpen(false)}
      transform={{ left: 10, top: transformHeight }}
    >
      <Stack direction="column">
        <AppSidebarItemWrapper>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">{user.name}</Typography>
            <Typography variant="caption">{user.roles?.map((u) => u.name).join(', ')}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <UserSwitchTenant tenants={tenants} tenantId={user.tenantId} onChange={handleOnTenantChange} />
          </Stack>
        </AppSidebarItemWrapper>
        <Divider />
        <AppSidebarItem
          icon={prefersDarkMode ? 'dark_mode' : 'light_mode'}
          label={
            <UserThemeSwitcher
              value={prefersDarkMode ? ThemeColorScheme.dark : ThemeColorScheme.light}
              onChange={handleThemeModeSwitch}
            />
          }
        />
        {isPersonalApiTokensEnabled && (
          <AppSidebarItem icon="passkey" label="Personal API Tokens" to={routes.PersonalApiTokens.path} />
        )}
        {isRequireMfaEnabled && (
          <AppSidebarItem icon="page_info" label="Preferences" onClick={() => showAdminPortal()} />
        )}
        <AppSidebarItem icon="admin" label="Go to Admin Portal" to={import.meta.env.VITE_ADMIN_PORTAL_URL} newTab />
        <AppSidebarItem icon="import_contacts" label="Docs" to="https://docs.apono.io" />
        <AppSidebarItem icon="logout" label="Log Out" to={getLogoutUrl()} color="error" />
      </Stack>
    </Popover>
  );
}
